import { DateUtil } from "@/utils/date.util";
import { ShoppingCartExtrasBundleDto, UpdateShoppingCartExtrasBundleDto } from "./extras.model";
import {
	ShoppingCartBundlePromotionsAndContractsDto,
	ShoppingCartBundlesPromotionsAndContractsDto,
	ShoppingCartConfigurationCharacteristicResDto,
	ShoppingCartConfigurationPriceResDto,
	UpdateShoppingCartBundleContractDto,
	UpdateShoppingCartBundlePromotionsDto,
	UpdateShoppingCartCharacteristicDto,
	UpdateShoppingCartCharacteristicPriceDto,
	UpdateShoppingCartContractsDto,
	UpdateShoppingCartPromotionsDto
} from "./model";
import { ExtendedShoppingCartBundlePromotionsAndContracts, ExtendedShoppingCartBundlesPromotionsAndContracts } from "@/models/shopping-cart.model";

const extendShoppingCartBundlePromotionsAndContractsDto = (
	bundle: ShoppingCartBundlePromotionsAndContractsDto
): ExtendedShoppingCartBundlePromotionsAndContracts => {
	const { contracts = [] } = bundle;

	return {
		...bundle,
		selectedContract: contracts.find((contract) => contract.qty === 1)
	};
};

function extendShoppingCartBundlesPromotionsAndContractsDto(
	payload: ShoppingCartBundlesPromotionsAndContractsDto
): ExtendedShoppingCartBundlesPromotionsAndContracts {
	const { id, bundles = [], parameters } = payload;

	return {
		id,
		bundles: bundles.map(extendShoppingCartBundlePromotionsAndContractsDto),
		parameters
	};
}

function mapShoppingCartExtrasBundleDtoToUpdateDto(bundle: ShoppingCartExtrasBundleDto): UpdateShoppingCartExtrasBundleDto {
	const { name, extras, extraGroups } = bundle;
	return { name, extras, extraGroups };
}

function mapShoppingCartConfigurationPriceResDtoToUpdateDto(price: ShoppingCartConfigurationPriceResDto): UpdateShoppingCartCharacteristicPriceDto {
	const { name, amount, displayName, bindingDate } = price;

	return {
		name,
		amount,
		displayName,
		bindingDate: bindingDate ? DateUtil.mapPromotionBindingDate({ bindingDate }) : undefined
	};
}

function mapShoppingCartConfigurationCharacteristicResDtoToUpdateDto(
	characteristic: ShoppingCartConfigurationCharacteristicResDto
): UpdateShoppingCartCharacteristicDto {
	const { name, qty, prices = [] } = characteristic;
	if (qty === 0 || prices.length === 0) {
		return { name, qty };
	}

	const formattedPrices = prices
		.map(mapShoppingCartConfigurationPriceResDtoToUpdateDto)
		.filter((price) => price.amount || price.bindingDate || price.displayName);

	return {
		name,
		qty,
		prices: formattedPrices.length > 0 ? formattedPrices : undefined
	};
}

function mapExtendedShoppingCartBundlesPromotionsAndContractsToPromotionUpdateDto({
	bundles = [],
	applyDefaultContract
}: {
	bundles: ExtendedShoppingCartBundlePromotionsAndContracts[];
	applyDefaultContract: boolean;
}): UpdateShoppingCartBundlePromotionsDto[] {
	return bundles.map((bundle) => {
		const { name, promotions: { characteristicGroups = [], characteristics = [] } = {} } = bundle;

		const promotions = [
			...characteristicGroups
				.map((group) => group.characteristics ?? [])
				.flat()
				.map(mapShoppingCartConfigurationCharacteristicResDtoToUpdateDto),
			...characteristics.map(mapShoppingCartConfigurationCharacteristicResDtoToUpdateDto)
		];

		return {
			name,
			applyDefaultContract,
			promotions
		};
	});
}

function buildUpdateShoppingCartPromotionsDto({
	bundles = [],
	applyDefaultContract
}: {
	bundles: ExtendedShoppingCartBundlePromotionsAndContracts[];
	applyDefaultContract: boolean;
}): UpdateShoppingCartPromotionsDto {
	return {
		bundles: mapExtendedShoppingCartBundlesPromotionsAndContractsToPromotionUpdateDto({ bundles, applyDefaultContract })
	};
}

function mapExtendedShoppingCartBundlesPromotionsAndContractsToContractUpdateDto(
	bundle: ExtendedShoppingCartBundlePromotionsAndContracts
): UpdateShoppingCartBundleContractDto {
	const { name, contracts = [], selectedContract } = bundle;
	const contract = contracts.find((contract) => contract.qty > 0);
	if (contract) {
		return { name, contract: { name: contract.name, qty: contract.qty } };
	}

	// TODO: remove selected contract from interface
	if (!selectedContract || (selectedContract && !selectedContract.qty)) {
		return { name };
	}

	const { name: contractName, qty: contractQty } = selectedContract;

	return { name, contract: { name: contractName, qty: contractQty } };
}

function buildUpdateShoppingCartContractsDto({
	bundles = []
}: {
	bundles: ExtendedShoppingCartBundlePromotionsAndContracts[];
}): UpdateShoppingCartContractsDto {
	const bundlesWithContracts = bundles.filter((bundle) => {
		const { contracts = [] } = bundle;

		return contracts.length;
	});

	return {
		bundles: bundlesWithContracts.map(mapExtendedShoppingCartBundlesPromotionsAndContractsToContractUpdateDto)
	};
}

export const ShoppingCartAdapter = {
	buildUpdateShoppingCartContractsDto,
	buildUpdateShoppingCartPromotionsDto,
	extendShoppingCartBundlesPromotionsAndContractsDto,
	mapShoppingCartExtrasBundleDtoToUpdateDto
};
